/**
 * /approve-login/:token — the "Approve sign-in" link from the login email.
 * Opening it approves nothing (mail scanners open links); the person sees
 * which device is asking and taps Approve. That signs in the waiting browser
 * and this device too.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldX, Monitor, Clock, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ApproveLogin() {
  const { token } = useParams();
  const nav = useNavigate();
  const { acceptSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);   // "denied" | "expired"

  useEffect(() => {
    api.post("/auth/login-request/inspect", { token })
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ status: "expired" }));
  }, [token]);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api.post("/auth/login-request/decide", { token, approve });
      if (r.data.status === "approved") {
        acceptSession(r.data);
        toast.success("Approved — you're signed in");
        nav("/app/dashboard", { replace: true });
        return;
      }
      setDone("denied");
    } catch (err) {
      setDone("expired");
    } finally { setBusy(false); }
  };

  const state = done || info?.status;
  return (
    <div className="min-h-screen grid place-items-center bg-[var(--sw-bg)] p-6" data-testid="approve-login-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-6">
        {!info ? (
          <div className="py-8 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : state === "pending" ? (
          <>
            <div className="w-11 h-11 rounded-full bg-emerald-50 grid place-items-center mb-3"><ShieldCheck className="text-emerald-600" size={22} /></div>
            <h1 className="text-[19px] font-bold text-slate-900">Approve this sign-in?</h1>
            <p className="text-[13px] text-slate-500 mt-1">Brainydo account {info.email}</p>
            <div className="mt-4 space-y-2 text-[13px] text-slate-700 p-3 rounded-lg bg-slate-50 border border-slate-200">
              {info.device && <div className="flex items-center gap-2"><Monitor size={14} className="text-slate-400" /> {info.device}</div>}
              {info.ip && <div className="flex items-center gap-2"><Globe size={14} className="text-slate-400" /> IP {info.ip}</div>}
              <div className="flex items-center gap-2"><Clock size={14} className="text-slate-400" /> {new Date(info.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" })}</div>
            </div>
            <p className="text-[12px] text-slate-500 mt-3">Only approve if you just tried to sign in. This also signs you in on this device.</p>
            <button onClick={() => decide(true)} disabled={busy} className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[14px] font-semibold disabled:opacity-60" data-testid="approve-login-btn">
              {busy ? "Approving…" : "Approve & sign in"}
            </button>
            <button onClick={() => decide(false)} disabled={busy} className="mt-2 w-full h-10 border border-slate-200 hover:border-slate-400 rounded-lg text-[13.5px] text-slate-700" data-testid="deny-login-btn">
              Not me — block it
            </button>
          </>
        ) : state === "denied" ? (
          <>
            <div className="w-11 h-11 rounded-full bg-red-50 grid place-items-center mb-3"><ShieldX className="text-red-600" size={22} /></div>
            <h1 className="text-[18px] font-bold text-slate-900">Sign-in blocked</h1>
            <p className="text-[13px] text-slate-500 mt-1">Nobody was signed in. Your account is safe.</p>
          </>
        ) : (
          <>
            <h1 className="text-[18px] font-bold text-slate-900">This request has expired</h1>
            <p className="text-[13px] text-slate-500 mt-1">Sign-in requests last 10 minutes and work once.</p>
            <Link to="/login" className="mt-4 inline-flex h-10 px-4 items-center bg-slate-900 text-white rounded-lg text-[13.5px]">Go to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
